import { luxembourgCivilDate } from './consultation-type-prices.js';
import { ageInMonthsAt } from './pediatric/cranial.js';

/**
 * Âge affiché d'un patient. Une seule règle pour tous les écrans et les textes du serveur :
 * en jours avant un mois, en mois jusqu'à deux ans — la pédiatrie raisonne en mois —, en années
 * ensuite. Les mois sont calendaires, ceux de `ageInMonthsAt`, la règle du bilan pédiatrique ;
 * les années en découlent, si bien qu'un enfant né un 29 février prend un an le 1er mars d'une
 * année commune, comme dans `firstBirthdayOf`.
 */

export const PATIENT_AGE_MONTHS_UNTIL = 24;

export type PatientAgeUnit = 'days' | 'months' | 'years';

export interface PatientAge {
  readonly unit: PatientAgeUnit;
  readonly value: number;
}

const DAY_MS = 86_400_000;

function civilTime(isoDate: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const time = Date.UTC(year, month - 1, day);
  return new Date(time).toISOString().slice(0, 10) === isoDate ? time : null;
}

/**
 * Âge à la date `onDate`, toutes deux `YYYY-MM-DD`. `null` si la date de naissance est absente,
 * illisible, ou postérieure à `onDate`.
 */
export function patientAgeAt(birthDate: string | null, onDate: string): PatientAge | null {
  if (birthDate === null) return null;
  const born = civilTime(birthDate);
  const at = civilTime(onDate);
  if (born === null || at === null || at < born) return null;
  const months = ageInMonthsAt(birthDate, onDate);
  if (months === null) return null;
  if (months < 1) return { unit: 'days', value: Math.round((at - born) / DAY_MS) };
  if (months < PATIENT_AGE_MONTHS_UNTIL) return { unit: 'months', value: months };
  return { unit: 'years', value: Math.floor(months / 12) };
}

/** Âge au jour civil du cabinet (Europe/Luxembourg) de l'instant `now`. */
export function patientAgeOn(birthDate: string | null, now: Date): PatientAge | null {
  return patientAgeAt(birthDate, luxembourgCivilDate(now));
}

/** Années révolues à la date `onDate`, pour les règles métier qui raisonnent en ans. */
export function ageInYearsAt(birthDate: string | null, onDate: string): number | null {
  if (birthDate === null) return null;
  const months = ageInMonthsAt(birthDate, onDate);
  return months === null ? null : Math.floor(months / 12);
}
